import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import imageCompression from 'browser-image-compression';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Upload, X, Sparkles, Loader2, ChevronRight, Image, AlertCircle, MapPin } from 'lucide-react';

const API = '/api';

const INDIAN_CITIES = [
  'Agra','Ahmedabad','Ajmer','Aligarh','Allahabad','Amritsar','Aurangabad',
  'Bangalore','Bareilly','Bhopal','Bhubaneswar','Bikaner','Chandigarh',
  'Chennai','Coimbatore','Dehradun','Delhi','Dhanbad','Durgapur','Faridabad',
  'Ghaziabad','Goa','Gurgaon','Guwahati','Gwalior','Hyderabad','Indore',
  'Jabalpur','Jaipur','Jalandhar','Jammu','Jamshedpur','Jodhpur','Kanpur',
  'Kochi','Kolkata','Kota','Kozhikode','Lucknow','Ludhiana','Madurai',
  'Mangalore','Meerut','Mumbai','Mysore','Nagpur','Nashik','Navi Mumbai',
  'Noida','Patna','Pune','Raipur','Rajkot','Ranchi','Srinagar','Surat',
  'Thane','Tiruchirappalli','Tirupati','Udaipur','Vadodara','Varanasi',
  'Vijayawada','Visakhapatnam','Warangal'
];

function CityAutocomplete({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const wrapRef = useRef(null);

  const handleInput = (e) => {
    const val = e.target.value;
    onChange(val);
    if (val.length >= 1) {
      const q = val.toLowerCase();
      setSuggestions(INDIAN_CITIES.filter(c => c.toLowerCase().startsWith(q)).slice(0, 7));
      setOpen(true);
    } else {
      setSuggestions([]);
      setOpen(false);
    }
  };

  const pick = (city) => { onChange(city); setOpen(false); setSuggestions([]); };

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          value={value}
          onChange={handleInput}
          onFocus={() => { if (suggestions.length) setOpen(true); }}
          placeholder="e.g. Mumbai"
          autoComplete="off"
          className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          data-testid="city-input"
          required
        />
      </div>
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto">
          {suggestions.map(city => (
            <li key={city}
              onMouseDown={() => pick(city)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-blue-50 cursor-pointer transition-colors">
              <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />{city}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const PROPERTY_TYPES = {
  residential: ['apartment', 'villa', 'duplex', 'triplex', 'farmhouse'],
  commercial: ['office', 'shop', 'showroom', 'godown'],
  industrial: ['industrial_shed', 'open_land'],
  pg: ['pg']
};
const BHK_OPTIONS = ['1 RK', '1 BHK', '2 BHK', '3 BHK', '4 BHK', '4+ BHK'];

const RESIDENTIAL_AMENITIES = [
  'Lift', 'Parking', 'Power Backup', 'Security/Guard', 'CCTV',
  'Swimming Pool', 'Gym', 'Garden', 'Children Play Area', 'Club House',
  'Intercom', 'Water Storage', 'Fire Safety', 'Sports Facility',
  'Visitor Parking', 'Rainwater Harvesting', 'Waste Management',
];

const INITIAL_FORM = {
  title: '', description: '',
  city: '', area: '', pincode: '',
  property_category: 'residential', property_type: 'apartment',
  bhk_type: '', floor_details: '',
  rent: '', maintenance: '', security_deposit: '',
  available_from: '', furnishing: 'unfurnished',
  bachelor_allowed: true, pets_allowed: false,
  pg_type: '', food_included: false, sharing_type: '',
  carpet_area: '',
  amenities: [],
  images: []
};

export default function AddPropertyPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = !!id;

  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTitleLoading, setAiTitleLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isEdit && id) {
      fetch(`${API}/properties/${id}`)
        .then(r => r.json())
        .then(data => {
          setForm(prev => ({ ...INITIAL_FORM, ...data }));
        })
        .catch(() => toast.error('Could not load property'));
    }
  }, [id, isEdit]);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleImageFiles = useCallback(async (files) => {
    const current = form.images.length;
    const remaining = 7 - current;
    if (remaining <= 0) { toast.error('Maximum 7 images allowed'); return; }
    const toProcess = Array.from(files).slice(0, remaining).filter(f => f.type.startsWith('image/'));
    if (!toProcess.length) return;

    setCompressing(true);

    // Check if Cloudinary is available for this upload session
    let cloudConfig = null;
    try {
      const st = await fetch(`${API}/cloudinary/status`, { credentials: 'same-origin' });
      const stData = await st.json();
      if (stData.configured) {
        const sig = await fetch(`${API}/cloudinary/signature?folder=properties/images`, { credentials: 'same-origin' });
        if (sig.ok) cloudConfig = await sig.json();
      }
    } catch { /* fall through to Base64 */ }

    for (const file of toProcess) {
      try {
        const compressed = await imageCompression(file, {
          maxSizeMB: cloudConfig ? 2 : 0.4,
          maxWidthOrHeight: cloudConfig ? 1600 : 1024,
          useWebWorker: true,
          fileType: 'image/jpeg',
          initialQuality: 0.85,
        });

        if (cloudConfig) {
          // Upload directly to Cloudinary
          const fd = new FormData();
          fd.append('file', compressed);
          fd.append('api_key', cloudConfig.api_key);
          fd.append('timestamp', String(cloudConfig.timestamp));
          fd.append('signature', cloudConfig.signature);
          fd.append('folder', cloudConfig.folder);
          const up = await fetch(
            `https://api.cloudinary.com/v1_1/${cloudConfig.cloud_name}/image/upload`,
            { method: 'POST', body: fd }
          );
          if (!up.ok) throw new Error('Cloudinary upload failed');
          const upData = await up.json();
          setForm(prev => ({ ...prev, images: [...prev.images, upData.secure_url].slice(0, 7) }));
        } else {
          // Fallback: Base64
          const reader = new FileReader();
          reader.onload = (e) => {
            setForm(prev => ({ ...prev, images: [...prev.images, e.target.result].slice(0, 7) }));
          };
          reader.readAsDataURL(compressed);
        }
      } catch {
        // Last-resort fallback to original file as Base64
        const reader = new FileReader();
        reader.onload = (e) => {
          setForm(prev => ({ ...prev, images: [...prev.images, e.target.result].slice(0, 7) }));
        };
        reader.readAsDataURL(file);
      }
    }
    setCompressing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.images.length]);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleImageFiles(e.dataTransfer.files);
  };

  const removeImage = (idx) => {
    setForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const generateTitle = async () => {
    if (!form.city) { toast.error('Please fill in city first'); return; }
    setAiTitleLoading(true);
    set('title', '');
    try {
      const res = await fetch(`${API}/ai/generate-title`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          property_type: form.property_type,
          property_category: form.property_category,
          bhk_type: form.bhk_type,
          city: form.city,
          area: form.area,
          furnishing: form.furnishing,
          amenities: form.amenities,
          bachelor_allowed: form.bachelor_allowed,
          pets_allowed: form.pets_allowed,
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to generate title');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        accumulated += decoder.decode(value, { stream: true });
        set('title', accumulated);
      }
      toast.success('Title generated!');
    } catch (err) {
      toast.error(err.message || 'Failed to generate title');
    } finally {
      setAiTitleLoading(false);
    }
  };

  const generateDescription = async () => {
    if (!form.city) { toast.error('Please fill in city first'); return; }
    setAiLoading(true);
    set('description', '');
    try {
      const res = await fetch(`${API}/ai/generate-description`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          property_type: form.property_type,
          property_category: form.property_category,
          bhk_type: form.bhk_type,
          city: form.city,
          area: form.area,
          furnishing: form.furnishing,
          rent: form.rent,
          carpet_area: form.carpet_area,
          floor_details: form.floor_details,
          bachelor_allowed: form.bachelor_allowed,
          pets_allowed: form.pets_allowed,
          food_included: form.food_included,
          pg_type: form.pg_type,
          sharing_type: form.sharing_type,
          amenities: form.amenities,
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to generate description');
      }

      // Stream tokens into the textarea in real time
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        accumulated += decoder.decode(value, { stream: true });
        set('description', accumulated);
      }

      toast.success('Description generated! Feel free to edit it.');
    } catch (err) {
      toast.error(err.message || 'Failed to generate description');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('Property title is required'); return; }
    if (!form.city) { toast.error('City is required'); return; }
    if (!form.rent) { toast.error('Monthly rent is required'); return; }
    if (form.images.length < 2) { toast.error('Please upload at least 2 images'); return; }

    setLoading(true);
    try {
      const url = isEdit ? `${API}/properties/${id}` : `${API}/properties`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ ...form, rent: parseFloat(form.rent), maintenance: parseFloat(form.maintenance || 0), security_deposit: parseFloat(form.security_deposit || 0) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      toast.success(isEdit ? 'Property updated! Pending review.' : 'Property submitted for review!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    }
    setLoading(false);
  };

  const isPG = form.property_category === 'pg';
  const isResidential = form.property_category === 'residential';
  const isIndustrial = form.property_category === 'industrial';

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>List Your Property | Rent Mitra</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-7">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>
            {isEdit ? 'Edit Property' : 'List Your Property'}
          </h1>
          <p className="text-slate-500 text-sm mt-1">Fill in the details below. All listings are reviewed before going live.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="add-property-form">
          {/* Section: Basic Info */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-900 mb-5 flex items-center gap-2" style={{fontFamily:'Outfit,sans-serif'}}>
              <span className="w-7 h-7 bg-blue-700 text-white rounded-lg flex items-center justify-center text-xs font-bold">1</span>
              Basic Information
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-slate-700">Property Title <span className="text-red-500">*</span></label>
                  <button type="button" onClick={generateTitle} disabled={aiTitleLoading || !form.city}
                    title={!form.city ? 'Fill in city first' : 'Generate title with AI'}
                    className="flex items-center gap-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 px-2.5 py-1 rounded-lg transition-colors border border-purple-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="ai-title-button">
                    {aiTitleLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {aiTitleLoading ? 'Writing...' : 'AI Title'}
                  </button>
                </div>
                <input value={form.title} onChange={e => set('title', e.target.value)}
                  placeholder="e.g. Spacious 2 BHK with Gym in Bandra West"
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm outline-none transition-all ${
                    aiTitleLoading
                      ? 'border-purple-300 ring-2 ring-purple-100 bg-purple-50/30'
                      : 'border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                  }`}
                  data-testid="title-input" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Category <span className="text-red-500">*</span></label>
                  <select value={form.property_category} onChange={e => { set('property_category', e.target.value); set('property_type', PROPERTY_TYPES[e.target.value]?.[0] || ''); }}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 bg-white" data-testid="category-select">
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="industrial">Industrial</option>
                    <option value="pg">PG / Hostel</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Property Type <span className="text-red-500">*</span></label>
                  <select value={form.property_type} onChange={e => set('property_type', e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 bg-white" data-testid="type-select">
                    {(PROPERTY_TYPES[form.property_category] || []).map(t => (
                      <option key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Location */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-900 mb-5 flex items-center gap-2" style={{fontFamily:'Outfit,sans-serif'}}>
              <span className="w-7 h-7 bg-blue-700 text-white rounded-lg flex items-center justify-center text-xs font-bold">2</span>
              Location
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">City <span className="text-red-500">*</span></label>
                <CityAutocomplete value={form.city} onChange={v => set('city', v)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Pincode</label>
                <input value={form.pincode} onChange={e => set('pincode', e.target.value.replace(/\D/g,'').slice(0,6))}
                  placeholder="400001" className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  data-testid="pincode-input" />
              </div>
              <div className="col-span-2 sm:col-span-3">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Area / Locality</label>
                <input value={form.area} onChange={e => set('area', e.target.value)}
                  placeholder="e.g. Bandra West, Koramangala" className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  data-testid="area-input" />
              </div>
            </div>
          </div>

          {/* Section: Pricing & Availability */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-900 mb-5 flex items-center gap-2" style={{fontFamily:'Outfit,sans-serif'}}>
              <span className="w-7 h-7 bg-blue-700 text-white rounded-lg flex items-center justify-center text-xs font-bold">3</span>
              Pricing & Availability
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: 'Monthly Rent (₹)', key: 'rent', placeholder: '25000', required: true },
                { label: 'Maintenance (₹)', key: 'maintenance', placeholder: '2000' },
                { label: 'Security Deposit (₹)', key: 'security_deposit', placeholder: '50000' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                  <input type="number" value={form[f.key]} onChange={e => set(f.key, e.target.value)}
                    placeholder={f.placeholder} min="0"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    data-testid={`${f.key}-input`} required={f.required} />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Available From</label>
                <input type="date" value={form.available_from} onChange={e => set('available_from', e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  data-testid="available-from-input" />
              </div>
            </div>
          </div>

          {/* Section: Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-900 mb-5 flex items-center gap-2" style={{fontFamily:'Outfit,sans-serif'}}>
              <span className="w-7 h-7 bg-blue-700 text-white rounded-lg flex items-center justify-center text-xs font-bold">4</span>
              Property Details
            </h3>
            <div className="space-y-4">
              {/* BHK — residential & PG */}
              {(isResidential || isPG) && (
                <div className="grid grid-cols-2 gap-4">
                  {isResidential && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">BHK Type</label>
                      <div className="flex flex-wrap gap-2">
                        {BHK_OPTIONS.map(b => (
                          <button key={b} type="button" onClick={() => set('bhk_type', form.bhk_type === b ? '' : b)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${form.bhk_type === b ? 'bg-blue-700 text-white border-blue-700' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`}
                            data-testid={`bhk-${b.replace(' ','-')}`}>
                            {b}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Floor Details</label>
                    <input value={form.floor_details} onChange={e => set('floor_details', e.target.value)}
                      placeholder="e.g. 3rd of 10 floors" className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      data-testid="floor-input" />
                  </div>
                </div>
              )}

              {/* Carpet Area — ALL categories */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Carpet Area <span className="text-slate-400 font-normal">(sq. ft.)</span>
                </label>
                <input
                  type="number" min="0" value={form.carpet_area}
                  onChange={e => set('carpet_area', e.target.value)}
                  placeholder="e.g. 850"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  data-testid="carpet-area-input"
                />
              </div>

              {/* Furnishing — residential, commercial, PG (NOT industrial) */}
              {!isPG && !isIndustrial && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Furnishing</label>
                  <div className="flex gap-2 flex-wrap">
                    {['furnished', 'semi-furnished', 'unfurnished'].map(f => (
                      <button key={f} type="button" onClick={() => set('furnishing', f)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all capitalize ${form.furnishing === f ? 'bg-blue-700 text-white border-blue-700' : 'border-slate-200 text-slate-600 hover:border-blue-300'}`}
                        data-testid={`furnishing-${f}`}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Bachelor / Pets — RESIDENTIAL only */}
              {isResidential && (
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.bachelor_allowed} onChange={e => set('bachelor_allowed', e.target.checked)}
                      className="w-4 h-4 rounded text-blue-700 accent-blue-700" data-testid="bachelor-checkbox" />
                    <span className="text-sm text-slate-700">Bachelor Allowed</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.pets_allowed} onChange={e => set('pets_allowed', e.target.checked)}
                      className="w-4 h-4 rounded text-blue-700 accent-blue-700" data-testid="pets-checkbox" />
                    <span className="text-sm text-slate-700">Pets Allowed</span>
                  </label>
                </div>
              )}

              {/* Amenities — RESIDENTIAL ONLY */}
              {isResidential && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Amenities</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="amenities-section">
                    {RESIDENTIAL_AMENITIES.map(a => {
                      const checked = form.amenities.includes(a);
                      return (
                        <label key={a} className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-all text-sm ${
                          checked ? 'bg-blue-50 border-blue-300 text-blue-800' : 'border-slate-200 text-slate-600 hover:border-blue-200'
                        }`}>
                          <input type="checkbox" checked={checked}
                            onChange={() => set('amenities', checked ? form.amenities.filter(x => x !== a) : [...form.amenities, a])}
                            className="w-3.5 h-3.5 accent-blue-700" />
                          {a}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* PG-specific fields */}
              {isPG && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-slate-100">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">PG Type</label>
                    <select value={form.pg_type} onChange={e => set('pg_type', e.target.value)}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 bg-white" data-testid="pg-type-select">
                      <option value="">Select</option>
                      <option value="boys">Boys</option>
                      <option value="girls">Girls</option>
                      <option value="co-living">Co-Living</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Sharing Type</label>
                    <select value={form.sharing_type} onChange={e => set('sharing_type', e.target.value)}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 bg-white" data-testid="sharing-type-select">
                      <option value="">Select</option>
                      <option value="single">Single</option>
                      <option value="double">Double Sharing</option>
                      <option value="triple">Triple Sharing</option>
                    </select>
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.food_included} onChange={e => set('food_included', e.target.checked)}
                        className="w-4 h-4 rounded text-blue-700 accent-blue-700" data-testid="food-checkbox" />
                      <span className="text-sm text-slate-700">Food Included</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section: Images */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2" style={{fontFamily:'Outfit,sans-serif'}}>
              <span className="w-7 h-7 bg-blue-700 text-white rounded-lg flex items-center justify-center text-xs font-bold">5</span>
              Photos
            </h3>
            <p className="text-xs text-slate-500 mb-4 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
              Min 2, Max 7 images. Auto-compressed to ~400KB for fast loading.
            </p>

            {/* Drop Zone */}
            <div
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => !compressing && fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all mb-4 ${
                isDragging ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
              } ${form.images.length >= 7 || compressing ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="image-dropzone"
            >
              <input ref={fileInputRef} type="file" multiple accept="image/*" className="hidden"
                onChange={e => handleImageFiles(e.target.files)} disabled={form.images.length >= 7 || compressing} />
              {compressing ? (
                <>
                  <Loader2 className="w-8 h-8 text-blue-500 mx-auto mb-2 animate-spin" />
                  <p className="text-sm font-medium text-blue-600">Compressing images...</p>
                  <p className="text-xs text-slate-400 mt-1">Optimizing for fast upload</p>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-600">Drag & drop or click to upload</p>
                  <p className="text-xs text-slate-400 mt-1">{form.images.length}/7 images uploaded</p>
                </>
              )}
            </div>

            {/* Image Preview */}
            {form.images.length > 0 && (
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-2" data-testid="image-previews">
                {form.images.map((img, i) => (
                  <div key={`img-${i}-${img.slice(-8)}`} className="relative group rounded-xl overflow-hidden aspect-square bg-slate-100">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeImage(i)}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-image-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                    {i === 0 && <span className="absolute bottom-1 left-1 bg-blue-700 text-white text-[9px] px-1 py-0.5 rounded font-bold">Main</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section: Description */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-900 mb-5 flex items-center gap-2" style={{fontFamily:'Outfit,sans-serif'}}>
              <span className="w-7 h-7 bg-blue-700 text-white rounded-lg flex items-center justify-center text-xs font-bold">6</span>
              Description
            </h3>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">Property Description</label>
              <button type="button" onClick={generateDescription} disabled={aiLoading}
                className="flex items-center gap-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 px-3 py-1.5 rounded-lg transition-colors border border-purple-200 disabled:opacity-60"
                data-testid="ai-generate-button">
                {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {aiLoading ? 'Writing...' : 'Write with AI'}
              </button>
            </div>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={5}
              placeholder={aiLoading ? '' : 'Describe your property — highlights, nearby amenities, etc. Or click "Write with AI" to auto-generate.'}
              className={`w-full px-4 py-3 border rounded-xl text-sm outline-none resize-none transition-all ${
                aiLoading
                  ? 'border-purple-300 ring-2 ring-purple-100 bg-purple-50/30'
                  : 'border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
              }`}
              data-testid="description-input" />
          </div>

          {/* Submit */}
          <div className="flex gap-3">
            <button type="button" onClick={() => navigate('/dashboard')}
              className="px-6 py-3 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors"
              data-testid="submit-property-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              {loading ? 'Submitting...' : isEdit ? 'Update Property' : 'Submit for Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
